import { createTheme } from '@mui/material/styles';

// Detect Edge browser
const isEdge = typeof navigator !== 'undefined' && /Edg\//.test(navigator.userAgent);
const isChrome = typeof navigator !== 'undefined' && /Chrome\//.test(navigator.userAgent) && !isEdge;

// CSS zoom factors (must match index.css)
// const cssZoomFactor = isEdge ? 1.15 : (isChrome ? 0.95 : 1);
const cssZoomFactor = 1; // Forced to 1 for testing
// Inverse zoom for dropdown compensation
const inverseZoomFactor = cssZoomFactor === 1 ? 1 : 1 / cssZoomFactor;

// Note: CSS zoom in index.css now handles Edge scaling uniformly
// sizeMultiplier is kept at 1.0 for both browsers since zoom handles everything
const sizeMultiplier = isEdge ? 1.15 : (isChrome ? 0.95 : 1);

// Helper function to scale rem values
const scaledRem = (baseRem: number): string => `${(baseRem * sizeMultiplier).toFixed(4)}rem`;

// Helper function to scale px values
const scaledPx = (basePx: number): number => Math.round(basePx * sizeMultiplier);

// Scaled base spacing unit (MUI default is 8px)
const scaledSpacing = Math.round(8 * sizeMultiplier);

// Scaled drawer width for consistent sidebar sizing
const scaledDrawerWidth = Math.round(320 * sizeMultiplier);

// Export scaling utilities for use in other components
export { sizeMultiplier, scaledPx, scaledDrawerWidth, inverseZoomFactor };

/** Monospace stack for all tabular UI (MUI tables, AG Grid, native tables). */
export const TABLE_FONT_FAMILY =
  '"Roboto Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace';

const sharedTableComponents = {
  MuiTable: {
    styleOverrides: {
      root: {
        fontFamily: TABLE_FONT_FAMILY,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        fontFamily: TABLE_FONT_FAMILY,
      },
      head: {
        fontFamily: TABLE_FONT_FAMILY,
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: {
        fontFamily: TABLE_FONT_FAMILY,
      },
      toolbar: {
        fontFamily: TABLE_FONT_FAMILY,
      },
      selectLabel: {
        fontFamily: TABLE_FONT_FAMILY,
      },
      displayedRows: {
        fontFamily: TABLE_FONT_FAMILY,
      },
    },
  },
};

// Light theme colors
const lightColors = {
  primary: {
    main: '#0f2134', // Dark blue
    light: '#3f51b5',
    dark: '#0d1a2d',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#ccba9b', // Beige
    light: '#f5f0e6',
    dark: '#a08b6f',
    contrastText: '#000000',
  },
  background: {
    default: '#ffffff', // White background
    paper: '#ffffff',
  },
  text: {
    primary: '#212121', // Dark gray for readability
    secondary: '#757575', // Medium gray for secondary text
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// Dark theme colors
const darkColors = {
  primary: {
    main: '#ffff', // Light blue for dark mode
    light: '#020202ff',
    dark: '#0f2134',
    contrastText: '#000000',
  },
  secondary: {
    main: '#ccba9b', // Beige
    light: '#f5f0e6',
    dark: '#a08b6f',
    contrastText: '#000000',
  },
  background: {
    default: '#0f2134', // Dark background
    paper: '#07182bff',
  },
  text: {
    primary: '#ffffff', // White text
    secondary: '#b0b0b0', // Light gray for secondary text
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// Shared breakpoints matching 8" (~768px) and 14" (~1366px)
const sharedBreakpoints = {
  values: {
    xs: 0,
    sm: 480,
    md: 768,   // 8 inches - Tablet/Small Laptop start
    lg: 1366,  // 14 inches - Big Screen start
    xl: 1920,
  },
};

// Shared responsive typography
const sharedTypography = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontSize: scaledRem(2.25), // Big Screen (>14")
    fontWeight: 300,
    lineHeight: 1.2,
    letterSpacing: '-0.01562em',
    '@media (max-width:1366px)': { fontSize: scaledRem(1.8) }, // Small Screen (8"-14")
    '@media (max-width:768px)': { fontSize: scaledRem(1.75) }, // Mobile (<=8")
  },
  h2: {
    fontSize: scaledRem(1.875),
    fontWeight: 300,
    lineHeight: 1.3,
    letterSpacing: '-0.00833em',
    '@media (max-width:1366px)': { fontSize: scaledRem(1.5) },
    '@media (max-width:768px)': { fontSize: scaledRem(1.5) },
  },
  h3: {
    fontSize: scaledRem(1.625),
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: '0em',
    '@media (max-width:1366px)': { fontSize: scaledRem(1.4) },
    '@media (max-width:768px)': { fontSize: scaledRem(1.375) },
  },
  h4: {
    fontSize: scaledRem(1.375),
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: '0.00735em',
    '@media (max-width:1366px)': { fontSize: scaledRem(1.15) },
    '@media (max-width:768px)': { fontSize: scaledRem(1.125) },
  },
  h5: {
    fontSize: scaledRem(1.1875),
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0em',
    '@media (max-width:1366px)': { fontSize: scaledRem(1.05) },
    '@media (max-width:768px)': { fontSize: scaledRem(1.0) },
  },
  h6: {
    fontSize: scaledRem(1.0),
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.0075em',
    '@media (max-width:1366px)': { fontSize: scaledRem(0.9) },
    '@media (max-width:768px)': { fontSize: scaledRem(0.875) },
  },
  body1: {
    fontSize: scaledRem(0.9375),
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
    '@media (max-width:1366px)': { fontSize: scaledRem(0.875) },
  },
  body2: {
    fontSize: scaledRem(0.84375),
    fontWeight: 400,
    lineHeight: 1.43,
    letterSpacing: '0.01071em',
    '@media (max-width:1366px)': { fontSize: scaledRem(0.8) },
  },
  button: {
    fontSize: scaledRem(0.84375),
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: '0.02857em',
    textTransform: 'none' as const,
    '@media (max-width:1366px)': { fontSize: scaledRem(0.8125) },
  },
  subtitle1: {
    fontSize: scaledRem(1.0),
    fontWeight: 400,
    lineHeight: 1.75,
    letterSpacing: '0.00938em',
    '@media (max-width:1366px)': { fontSize: scaledRem(0.9) },
    '@media (max-width:768px)': { fontSize: scaledRem(0.875) },
  },
  subtitle2: {
    fontSize: scaledRem(0.875),
    fontWeight: 500,
    lineHeight: 1.57,
    letterSpacing: '0.00714em',
    '@media (max-width:1366px)': { fontSize: scaledRem(0.8) },
    '@media (max-width:768px)': { fontSize: scaledRem(0.75) },
  },
  caption: {
    fontSize: scaledRem(0.75),
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: '0.03333em',
    '@media (max-width:1366px)': { fontSize: scaledRem(0.7) },
    '@media (max-width:768px)': { fontSize: scaledRem(0.65) },
  },
  overline: {
    fontSize: scaledRem(0.75),
    fontWeight: 400,
    lineHeight: 2.66,
    letterSpacing: '0.08333em',
    textTransform: 'uppercase' as const,
    '@media (max-width:1366px)': { fontSize: scaledRem(0.65) },
    '@media (max-width:768px)': { fontSize: scaledRem(0.65) },
  },
};

// Create light theme
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    ...lightColors,
  },

  // Typography improvements
  typography: sharedTypography,

  // Component overrides for minimalistic design
  components: {
    ...sharedTableComponents,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: scaledPx(10),
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid rgba(0,0,0,0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: scaledPx(8),
          padding: `${scaledPx(8)}px ${scaledPx(16)}px`,
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          },
        },
        contained: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: scaledPx(8),
            '& fieldset': {
              borderColor: lightColors.grey[300],
            },
            '&:hover fieldset': {
              borderColor: lightColors.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderWidth: 2,
            },
          },
          '& .MuiOutlinedInput-input': {
            color: lightColors.text.primary,
            backgroundColor: 'transparent',
            // Override browser autocomplete styles
            '&:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 100px ${lightColors.background.paper} inset !important`,
              WebkitTextFillColor: `${lightColors.text.primary} !important`,
              caretColor: lightColors.text.primary,
            },
            '&:-webkit-autofill:hover': {
              WebkitBoxShadow: `0 0 0 100px ${lightColors.background.paper} inset !important`,
              WebkitTextFillColor: `${lightColors.text.primary} !important`,
            },
            '&:-webkit-autofill:focus': {
              WebkitBoxShadow: `0 0 0 100px ${lightColors.background.paper} inset !important`,
              WebkitTextFillColor: `${lightColors.text.primary} !important`,
            },
            '&:-webkit-autofill:active': {
              WebkitBoxShadow: `0 0 0 100px ${lightColors.background.paper} inset !important`,
              WebkitTextFillColor: `${lightColors.text.primary} !important`,
            },
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        root: {
          // Apply inverse zoom to compensate for body zoom
          zoom: inverseZoomFactor,
        },
        paper: {
          borderRadius: scaledPx(14),
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: lightColors.primary.main,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid rgba(0,0,0,0.08)',
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: scaledPx(8),
          margin: `${scaledPx(2)}px ${scaledPx(8)}px`,
          '&:hover': {
            backgroundColor: 'rgba(15, 33, 52, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(15, 33, 52, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(15, 33, 52, 0.16)',
            },
          },
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: scaledPx(8),
          boxShadow: 'none',
          border: '1px solid rgba(0,0,0,0.08)',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: scaledPx(8),
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.02)',
          },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: scaledPx(8),
        },
        standardSuccess: {
          backgroundColor: '#e8f5e8',
        },
        standardWarning: {
          backgroundColor: '#fff8e1',
        },
        standardError: {
          backgroundColor: '#ffebee',
        },
        standardInfo: {
          backgroundColor: '#e3f2fd',
        },
      },
    },

    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        root: {
          // Apply inverse zoom to compensate for body zoom and fix positioning
          zoom: inverseZoomFactor,
        },
      },
    },

    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        root: {
          // Apply inverse zoom to compensate for body zoom and fix positioning
          zoom: inverseZoomFactor,
        },
        paper: {
          marginTop: scaledPx(4),
        },
      },
    },

    MuiModal: {
      styleOverrides: {
        root: {
          // Apply inverse zoom to compensate for body zoom
          zoom: inverseZoomFactor,
        },
      },
    },

    MuiAutocomplete: {
      defaultProps: {
        slotProps: {
          popper: {
            sx: {
              // Apply inverse zoom to compensate for body zoom and fix dropdown positioning
              zoom: inverseZoomFactor,
            },
          },
        },
      },
    },
  },

  // Spacing system
  spacing: scaledSpacing,

  // Breakpoints for responsive design
  breakpoints: sharedBreakpoints,

  // Shape system
  shape: {
    borderRadius: scaledPx(8),
  },

  // Transitions
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
});

// Create dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    ...darkColors,
  },

  // Typography improvements
  typography: sharedTypography,

  // Component overrides for dark mode
  components: {
    ...sharedTableComponents,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: scaledPx(10),
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255,255,255,0.08)',
          backgroundColor: '#1a2e4a',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: scaledPx(8),
          padding: `${scaledPx(8)}px ${scaledPx(16)}px`,
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          },
        },
        contained: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: scaledPx(8),
            '& fieldset': {
              borderColor: darkColors.grey[300],
            },
            '&:hover fieldset': {
              borderColor: darkColors.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderWidth: 2,
            },
            '&.Mui-focused .MuiOutlinedInput-input': {
              backgroundColor: 'transparent',
              color: darkColors.text.primary,
            }
          },
          '& .MuiOutlinedInput-input': {
            color: darkColors.text.primary,
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: scaledPx(6),
          fontWeight: 500,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        root: {
          // Apply inverse zoom to compensate for body zoom
          zoom: inverseZoomFactor,
        },
        paper: {
          borderRadius: scaledPx(14),
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          backgroundColor: '#1a2e4a',
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: darkColors.primary.main,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid rgba(255,255,255,0.08)',
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: scaledPx(8),
          margin: `${scaledPx(2)}px ${scaledPx(8)}px`,
          '&:hover': {
            backgroundColor: 'rgba(15, 33, 52, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(15, 33, 52, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(15, 33, 52, 0.16)',
            },
          },
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: scaledPx(8),
          boxShadow: 'none',
          border: '1px solid rgba(255,255,255,0.08)',
          backgroundColor: '#1a2e4a',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: scaledPx(8),
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.02)',
          },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: scaledPx(8),
        },
        standardSuccess: {
          backgroundColor: '#1a2e4a',
          color: '#ffffff',
        },
        standardWarning: {
          backgroundColor: '#2e3b1f',
          color: '#ffffff',
        },
        standardError: {
          backgroundColor: '#3c1f1f',
          color: '#ffffff',
        },
        standardInfo: {
          backgroundColor: '#1a2e4a',
          color: '#ffffff',
        },
      },
    },

    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        root: {
          // Apply inverse zoom to compensate for body zoom and fix positioning
          zoom: inverseZoomFactor,
        },
      },
    },

    MuiMenu: {
      defaultProps: {
        disableScrollLock: true,
      },
      styleOverrides: {
        root: {
          // Apply inverse zoom to compensate for body zoom and fix positioning
          zoom: inverseZoomFactor,
        },
        paper: {
          marginTop: scaledPx(4),
        },
      },
    },

    MuiModal: {
      styleOverrides: {
        root: {
          // Apply inverse zoom to compensate for body zoom
          zoom: inverseZoomFactor,
        },
      },
    },

    MuiAutocomplete: {
      defaultProps: {
        slotProps: {
          popper: {
            sx: {
              // Apply inverse zoom to compensate for body zoom and fix dropdown positioning
              zoom: inverseZoomFactor,
            },
          },
        },
      },
    },
  },

  // Spacing system
  spacing: scaledSpacing,

  // Breakpoints for responsive design
  breakpoints: sharedBreakpoints,

  // Shape system
  shape: {
    borderRadius: scaledPx(8),
  },

  // Transitions
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
});

export { lightTheme, darkTheme };